import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import SEOMeta from '@/components/base/SEOMeta';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

const categoryLabelMap: Record<string, string> = {
  'platform-intro': '平台介绍',
  'usage-guide': '使用教程',
  'account-security': '账号安全',
  'latest-news': '最新动态',
  'faq-help': '常见问题',
  'community': '社区交流',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'platform-intro';
  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const filteredArticles = articles.filter((a) => a.category === activeSlug);

  const categoryName = currentCategory?.name || categoryLabelMap[activeSlug] || activeSlug;
  const categoryDesc = currentCategory?.description || '';

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${categoryName} - 91com官方平台`}
        description={`91com${categoryName}栏目，${categoryDesc}。为您提供全面详细的91com相关内容与指南。`}
        keywords={`91com,${categoryName},91com官网,91com平台介绍`}
        canonicalUrl={`${siteInfo.url}/category/${activeSlug}`}
        ogTitle={`${categoryName} - 91com官方平台`}
        ogDescription={`91com${categoryName}栏目，${categoryDesc}`}
      />
      <SchemaOrg pageType="category" categoryName={categoryName} />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: categoryName }]} />

          {/* Category Header */}
          <section className="py-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-600">{categoryDesc}</p>
          </section>

          {/* Category Tabs */}
          <section className="mb-8">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Articles Grid */}
          <section className="pb-14 md:pb-20">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}

            {filteredArticles.length > 0 && (
              <div className="mt-8 text-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回首页
                </Link>
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}